import { Button, Card, Col, DatePicker, Row, Select, Space, Table, Tag, Typography, message } from "antd";
import { LeftOutlined, RightOutlined, FileExcelOutlined } from "@ant-design/icons";
import PageContentWrapper from "../../components/PageContentWrapper";
import { useEffect, useState } from "react";
import moment from "moment";
import Widget from "components/Widget/index";
import * as XLSX from 'xlsx/xlsx.mjs';
import { baseRequest } from "../../util/Api";
import { getTrainerRecapitulation, getTrainingCategories, updateTrainerRecapitulation } from "../../appRedux/actions";
import { useDispatch, useSelector } from "react-redux";

export default function TrainerRecapitulation() {
  const COLOR_RUNNING = "#87d068";
  const COLOR_CANCEL = "#f50";
  const COLOR_SISA = "#808080";

  const queryParams = new URLSearchParams(window.location.search);
  const startParam = queryParams.get("start");
  const endParam = queryParams.get("end");

  const [dates, onChangeDates] = useState([
    startParam ? moment(startParam).toISOString() : moment().startOf("month").toISOString(),
    endParam ? moment(endParam).toISOString() : moment().endOf("month").toISOString(),
  ]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalDaysTraining, setTotalDaysTraining] = useState(0);
  const [totalDaysContract, setTotalDaysContract] = useState(0);
  const [totalDaysCancel, setTotalDaysCancel] = useState(0);
  const [totalDaysRunning, setTotalDaysRunning] = useState(0);
  const [averageUtilization, setAverageUtilization] = useState(0);

  const dispatch = useDispatch();
  const { trainingCategories, trainingCategoriesLoading } = useSelector(
    ({ trainingCategory }) => trainingCategory
  );

  useEffect(() => {
    dispatch(getTrainingCategories());
  }, [dispatch]);

  const handleUpdate = (trainer) => {
    updateTrainerRecapitulation(trainer).then(() => {
      loadData();
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      align: "center",
      width: 1,
      render: (id) => id,
    },
    {
      title: "Trainer",
      dataIndex: "name",
      render: (name) => name,
    },
    {
      title: "Spesialisasi",
      dataIndex: "training_category_id",
      render: (training_category_id, record) => {
        return (
          <Select
            loading={trainingCategoriesLoading}
            value={training_category_id}
            placeholder="pilih kategori"
            onChange={(value) => handleUpdate({ ...record, training_category_id: value })}
          >
            {trainingCategories.map((category) => (
              <Select.Option key={category.id} value={category.id}>
                {category.name}
              </Select.Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Kontrak",
      dataIndex: "contract",
      align: "center",
      width: 1,
      render: (contract, record) => (
        <Typography.Text
          editable={{
            onChange: (value) => {
              const val = parseInt(value);
              if (isNaN(val) || val < 0) {
                message.error("Nilai kontrak tidak boleh negatif atau kosong");
                return;
              }
              handleUpdate({ ...record, contract: val });
            },
          }}
        >
          {contract}
        </Typography.Text>
      ),
    },
    {
      title: "Hari Mengajar",
      dataIndex: "teaching_days_count",
      align: "center",
      width: 1,
      render: (teaching_days_count) => teaching_days_count || 0,
    },
    {
      title: "Running",
      dataIndex: "is_running_count",
      align: "center",
      width: 1,
      render: (is_running_count) => is_running_count || 0,
    },
    {
      title: "Cancel",
      dataIndex: "is_canceled_count",
      align: "center",
      width: 1,
      render: (is_canceled_count) => is_canceled_count || 0,
    },
    {
      title: "Sisa",
      dataIndex: "contract",
      align: "center",
      width: 1,
      render: (contract, record) => {
        return contract - record.teaching_days_count || 0;
      },
    },
    {
      title: "Utilisasi",
      dataIndex: "contract",
      align: "center",
      width: 1,
      render: (contract, record) => {
        if (!contract || contract === 0) return "-";
        const utilization = (record.is_running_count / contract) * 100;
        return `${utilization.toFixed(0)}%`;
      },
    },
    {
      title: "Visualisasi Bulan",
      dataIndex: "contract",
      render: (contract, record) => {
        if ((!contract || contract === 0) && record.teaching_days_count < 1) return "-";
        return <Space direction="vertical">
          <Space direction="horizontal" size={0} style={{
            borderRadius: '4px',
            overflow: 'hidden',
            width: '15em',
          }}>
            <div style={{
              backgroundColor: COLOR_RUNNING,
              margin: 0,
              borderRadius: 0,
              width: (record.is_running_count / contract) * 15 + "em"
            }}>&nbsp;</div>
            <div style={{
              backgroundColor: COLOR_CANCEL,
              margin: 0,
              borderRadius: 0,
              width: (record.is_canceled_count / contract) * 15 + "em"
            }}>&nbsp;</div>
            <div style={{
              backgroundColor: COLOR_SISA,
              margin: 0,
              borderRadius: 0,
              width: ((contract - record.teaching_days_count) / contract) * 15 + "em"
            }}>&nbsp;</div>
          </Space>
          <Row>
            <Col span={8} style={{ textAlign: 'left' }}>0</Col>
            <Col span={8} style={{ textAlign: 'center' }}>{(record.contract / 2).toFixed(0)}</Col>
            <Col span={8} style={{ textAlign: 'right' }}>{record.contract}</Col>
          </Row>
        </Space>
      },
    },
  ];

  const handlePrevMonth = () => {
    const start = moment(dates[0]).subtract(1, "months").startOf("month").toISOString();
    const end = moment(start).endOf("month").toISOString();
    onChangeDates([start, end]);
  };

  const handleNextMonth = () => {
    const start = moment(dates[0]).add(1, "months").startOf("month").toISOString();
    const end = moment(start).endOf("month").toISOString();
    onChangeDates([start, end]);
  };

  const handleExport = () => {
    const header = [
      "ID",
      "Trainer",
      "Spesialisasi",
      "Kontrak",
      "Hari Mengajar",
      "Running",
      "Cancel",
      "Sisa",
      "Utilisasi",
    ];

    const rows = data.map((record) => {
      const category = trainingCategories.find((c) => c.id === record.training_category_id);
      const utilization = record.contract > 0 ? ((record.is_running_count / record.contract) * 100).toFixed(0) + "%" : "-";
      return [
        record.id,
        record.name,
        category ? category.name : "-",
        record.contract || 0,
        record.teaching_days_count || 0,
        record.is_running_count || 0,
        record.is_canceled_count || 0,
        (record.contract || 0) - (record.teaching_days_count || 0),
        utilization,
      ];
    });

    // Add summary row at the bottom
    rows.push([
      "Total", "", "",
      totalDaysContract, totalDaysTraining, totalDaysRunning, totalDaysCancel,
      totalDaysContract - totalDaysTraining,
      totalDaysContract > 0 ? ((totalDaysRunning / totalDaysContract) * 100).toFixed(0) + "%" : "0%",
    ]);

    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Recapitulation");
    XLSX.writeFile(wb, `Trainer_Recapitulation_${moment(dates[0]).format("YYYY-MM-DD")}_to_${moment(dates[1]).format("YYYY-MM-DD")}.xlsx`);
  };

  const loadData = () => {
    setLoading(true);
    getTrainerRecapitulation({
      start: dates[0],
      end: dates[1],
    }).then((response) => {
      setData(response.data);
      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
    const params = new URLSearchParams(window.location.search);
    params.set("start", moment(dates[0]).format("YYYY-MM-DD"));
    params.set("end", moment(dates[1]).format("YYYY-MM-DD"));
    const newRelativePathQuery = window.location.pathname + '?' + params.toString();
    window.history.replaceState(null, '', newRelativePathQuery);
  }, [dates]);

  return (
    <PageContentWrapper title="Rekapitulasi Hari Mengajar Trainer" backLink={"/trainer"}
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Space align="center">
          <Button icon={<LeftOutlined />} onClick={handlePrevMonth} style={{ margin: 0 }} />
          <DatePicker.RangePicker
            format="D MMMM YYYY"
            onChange={(v) =>
              v
                ? onChangeDates(v.map((date) => date.toISOString()))
                : onChangeDates([
                  moment().startOf("month").toISOString(),
                  moment().endOf("month").toISOString(),
                ])
            }
            value={dates.map((date) => (date ? moment(date) : null))}
          />
          <Button
            icon={<RightOutlined />}
            onClick={handleNextMonth}
            style={{ margin: 0 }}
            // disabled={dates[1] >= moment().endOf("month").toISOString()}
          />
          <Button type="primary" icon={<FileExcelOutlined />} onClick={handleExport} style={{ margin: 0 }}>Export</Button>
        </Space>
        <Row>
          <Col xl={6} lg={12} md={12} sm={12} xs={24}>
            <Widget styleName={`gx-card-full`}>
              <div className={`gx-fillchart gx-bg-primary gx-overlay-fillchart`}>
                <div className="ant-card-head-title"></div>
                <div className="gx-fillchart-content">
                  <div className="ant-card-head-title gx-mb-3">Total Hari Running</div>
                  <Row wrap gutter={[16, 16]} justify="space-between">
                    <Col>
                      <h2 className="gx-mb-2 gx-fs-xxxl gx-font-weight-medium">{totalDaysRunning}</h2>
                      <p className="gx-mb-0 gx-fs-sm">dari {totalDaysContract} hari kontrak</p>
                    </Col>
                  </Row>
                </div>
              </div>
            </Widget>
          </Col>
          <Col xl={6} lg={12} md={12} sm={12} xs={24}>
            <Widget styleName={`gx-card-full`}>
              <div className={`gx-fillchart gx-bg-orange gx-overlay-fillchart`}>
                <div className="ant-card-head-title"></div>
                <div className="gx-fillchart-content">
                  <div className="ant-card-head-title gx-mb-3">Total Hari Cancel</div>
                  <Row wrap gutter={[16, 16]} justify="space-between">
                    <Col>
                      <h2 className="gx-mb-2 gx-fs-xxxl gx-font-weight-medium">{totalDaysCancel}</h2>
                      <p className="gx-mb-0 gx-fs-sm">{totalDaysContract > 0 ? ((totalDaysCancel / totalDaysContract) * 100).toFixed(2) : 0}% dari kontrak</p>
                    </Col>
                  </Row>
                </div>
              </div>
            </Widget>
          </Col>
          <Col xl={6} lg={12} md={12} sm={12} xs={24}>
            <Widget styleName={`gx-card-full`}>
              <div className={`gx-fillchart gx-bg-teal gx-overlay-fillchart`}>
                <div className="ant-card-head-title"></div>
                <div className="gx-fillchart-content">
                  <div className="ant-card-head-title gx-mb-3">Sisa/Belum Jalan</div>
                  <Row wrap gutter={[16, 16]} justify="space-between">
                    <Col>
                      <h2 className="gx-mb-2 gx-fs-xxxl gx-font-weight-medium">{totalDaysContract - totalDaysRunning - totalDaysCancel}</h2>
                      <p className="gx-mb-0 gx-fs-sm">hari belum terjadwal</p>
                    </Col>
                  </Row>
                </div>
              </div>
            </Widget>
          </Col>
          <Col xl={6} lg={12} md={12} sm={12} xs={24}>
            <Widget styleName={`gx-card-full`}>
              <div className={`gx-fillchart gx-bg-blue gx-overlay-fillchart`}>
                <div className="ant-card-head-title"></div>
                <div className="gx-fillchart-content">
                  <div className="ant-card-head-title gx-mb-3">Rata-rata Utilisasi</div>
                  <Row wrap gutter={[16, 16]} justify="space-between">
                    <Col>
                      <h2 className="gx-mb-2 gx-fs-xxxl gx-font-weight-medium">{averageUtilization.toFixed(0)}%</h2>
                      <p className="gx-mb-0 gx-fs-sm">running + kontrak</p>
                    </Col>
                  </Row>
                </div>
              </div>
            </Widget>
          </Col>
        </Row>
        <Table
          dataSource={data}
          columns={columns}
          loading={loading}
          pagination={false}
          rowKey="id"
          summary={(pageData) => {
            let totalContract = 0;
            let totalTeachingDays = 0;
            let totalRunning = 0;
            let totalCancel = 0;
            let utilization = 0;
            let counted = 0;

            pageData.forEach(({ contract, teaching_days_count, is_running_count, is_canceled_count }) => {
              totalContract += contract || 0;
              totalTeachingDays += teaching_days_count || 0;
              totalRunning += is_running_count || 0;
              totalCancel += is_canceled_count || 0;
              if (contract > 0) {
                utilization += (is_running_count / contract) * 100 || 0;
                counted++;
              }
            });
            setTotalDaysContract(totalContract);
            setTotalDaysTraining(totalTeachingDays);
            setTotalDaysCancel(totalCancel);
            setTotalDaysRunning(totalRunning);
            setAverageUtilization(counted > 0 ? utilization / counted : 0);

            return (
              <Table.Summary.Row style={{ background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={3} align="right">
                  <Typography.Text strong>Total</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="center">
                  <Typography.Text strong>{totalContract}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="center">
                  <Typography.Text strong>{totalTeachingDays}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="center">
                  <Typography.Text strong>{totalRunning}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="center">
                  <Typography.Text strong>{totalCancel}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="center">
                  <Typography.Text strong>{totalContract - totalTeachingDays}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="center">
                  <Typography.Text strong>{totalContract > 0 ? ((totalRunning / totalContract) * 100).toFixed(0) : 0}%</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} />
              </Table.Summary.Row>
            );
          }}
        />
        <Space size={18}>
          <Space size={4}><Tag color={COLOR_RUNNING} style={{ margin: 0 }}>&nbsp;&nbsp;</Tag> Running</Space>
          <Space size={4}><Tag color={COLOR_CANCEL} style={{ margin: 0 }}>&nbsp;&nbsp;</Tag> Cancel</Space>
          <Space size={4}><Tag color={COLOR_SISA} style={{ margin: 0 }}>&nbsp;&nbsp;</Tag> Sisa/Belum Jalan</Space>
        </Space>
      </Space>
    </PageContentWrapper>
  );
}